// src/dataAttributes/DataAttribute.ts
import { EncodedText } from "@clearstream/sms-encoding";
var _DataAttribute = class _DataAttribute {
  constructor(options) {
    this.options = options;
  }
  static getOptionKeys() {
    return [_DataAttribute.FALLBACK_OPTION, _DataAttribute.URL_SAFE_OPTION];
  }
  getOptions() {
    return this.options;
  }
  getDefaultMaxLengthBeforeCutOff() {
    return 2 * (this.isUrlSafe() ? this.getDefaultUrlSafeReserveLength() : this.getDefaultReserveLength());
  }
  getFallback() {
    return this.options.fallback ?? "";
  }
  isUrlSafe() {
    return this.options.urlsafe !== void 0;
  }
  getReserveLength() {
    const reserveLength = this.isUrlSafe() ? this.getDefaultUrlSafeReserveLength() : this.getDefaultReserveLength();
    const fallback = this.getValueConsideringUrlSafeOption(this.getFallback());
    const fallbackLength = new EncodedText(fallback).getLength();
    return Math.max(reserveLength, fallbackLength);
  }
  getMaxLengthBeforeCutOff() {
    return Math.max(
      this.getDefaultMaxLengthBeforeCutOff(),
      this.getReserveLength()
    );
  }
  getLongestValueForReserve() {
    const reserveLength = this.getReserveLength();
    const fallback = this.getFallback();
    let value;
    if (new EncodedText(
      this.getValueConsideringUrlSafeOption(fallback)
    ).getLength() >= reserveLength) {
      value = fallback;
    } else {
      value = new EncodedText(fallback).getEncoding().getSampleCharacter().repeat(reserveLength);
    }
    value = this.getValueConsideringMaxLength(value, reserveLength);
    return this.getValueConsideringUrlSafeOption(value);
  }
  getProcessedValueFromContext(context) {
    let value = this.getValueFromContext(context) ?? "";
    value = this.getValueConsideringFallback(value);
    value = this.getValueConsideringMaxLength(
      value,
      this.getMaxLengthBeforeCutOff()
    );
    return this.getValueConsideringUrlSafeOption(value);
  }
  getValueConsideringFallback(value) {
    return value === "" ? this.getFallback() : value;
  }
  getValueConsideringMaxLength(value, maxLength) {
    while (new EncodedText(
      this.getValueConsideringUrlSafeOption(value)
    ).getLength() > maxLength) {
      value = value.slice(0, -1);
    }
    return value;
  }
  getValueConsideringUrlSafeOption(value) {
    return this.isUrlSafe() ? encodeURIComponent(value) : value;
  }
};
_DataAttribute.FALLBACK_OPTION = "fallback";
_DataAttribute.URL_SAFE_OPTION = "urlsafe";
var DataAttribute = _DataAttribute;
var DataAttribute_default = DataAttribute;

// src/dataAttributes/FirstName.ts
var FirstName = class extends DataAttribute_default {
  getLabel() {
    return "First Name";
  }
  getDefaultReserveLength() {
    return 10;
  }
  getDefaultUrlSafeReserveLength() {
    return 15;
  }
  getValueFromContext(context) {
    return context.getFirstName();
  }
};
var FirstName_default = FirstName;

// src/dataAttributes/LastName.ts
var LastName = class extends DataAttribute_default {
  getLabel() {
    return "Last Name";
  }
  getDefaultReserveLength() {
    return 12;
  }
  getDefaultUrlSafeReserveLength() {
    return 18;
  }
  getValueFromContext(context) {
    return context.getLastName();
  }
};
var LastName_default = LastName;

// src/UnknownDataAttributeError.ts
var UnknownDataAttributeError = class extends Error {
};
var UnknownDataAttributeError_default = UnknownDataAttributeError;

// src/dataAttributes/FullName.ts
var FullName = class extends DataAttribute_default {
  getLabel() {
    return "Full Name";
  }
  getDefaultReserveLength() {
    return 25;
  }
  getDefaultUrlSafeReserveLength() {
    return 37;
  }
  getValueFromContext(context) {
    return context.getFullName();
  }
};
var FullName_default = FullName;

// src/dataAttributes/Mobile.ts
var Mobile = class extends DataAttribute_default {
  getLabel() {
    return "Mobile";
  }
  getDefaultReserveLength() {
    return 13;
  }
  getDefaultUrlSafeReserveLength() {
    return 15;
  }
  getValueFromContext(context) {
    return context.getMobile();
  }
};
var Mobile_default = Mobile;

// src/dataAttributes/RockPersonId.ts
var RockPersonId = class extends DataAttribute_default {
  getLabel() {
    return "Rock Person Id";
  }
  getDefaultReserveLength() {
    return 10;
  }
  getDefaultUrlSafeReserveLength() {
    return 10;
  }
  getValueFromContext(context) {
    return context.getRockPersonId();
  }
};
var RockPersonId_default = RockPersonId;

// src/dataAttributes/ContactKey.ts
var ContactKey = class extends DataAttribute_default {
  getLabel() {
    return "Contact Key";
  }
  getDefaultReserveLength() {
    return 8;
  }
  getDefaultUrlSafeReserveLength() {
    return 8;
  }
  getValueFromContext(context) {
    return context.getContactKey();
  }
};
var ContactKey_default = ContactKey;

// src/dataAttributes/Email.ts
var Email = class extends DataAttribute_default {
  getLabel() {
    return "Email";
  }
  getDefaultReserveLength() {
    return 35;
  }
  getDefaultUrlSafeReserveLength() {
    return 40;
  }
  getValueFromContext(context) {
    return context.getEmail();
  }
};
var Email_default = Email;

// src/DataAttributeMaker.ts
var _DataAttributeMaker = class _DataAttributeMaker {
  make(keyword, options) {
    for (const [
      dataAttributeKeyword,
      dataAttributeConstructor
    ] of Object.entries(_DataAttributeMaker.REGISTERED_DATA_ATTRIBUTES)) {
      if (dataAttributeKeyword.toLowerCase() === keyword.toLowerCase()) {
        return new dataAttributeConstructor(options);
      }
    }
    throw new UnknownDataAttributeError_default(
      `Unknown "${keyword}" data attribute encountered.`
    );
  }
  makeFromTagContent(tagContent) {
    const { keyword, options } = this.parseTagContent(tagContent);
    return this.make(keyword, options);
  }
  parseTagContent(tagContent) {
    const parts = tagContent.split("|");
    if (parts.length === 2 && !DataAttribute_default.getOptionKeys().some(
      (optionKey) => parts[1].trim().startsWith(optionKey)
    )) {
      return {
        keyword: parts[0].trim(),
        options: parts[1].trim() === "" ? {} : { fallback: parts[1].trim() }
      };
    }
    return {
      keyword: parts[0].trim(),
      options: parts.slice(1).reduce(
        (options, part) => {
          const optionKey = part.split(":")[0].trim();
          const optionValue = part.split(":").slice(1).join(":").trim();
          if (optionKey !== "") {
            options[optionKey] = optionValue;
          }
          return options;
        },
        {}
      )
    };
  }
};
_DataAttributeMaker.REGISTERED_DATA_ATTRIBUTES = {
  first: FirstName_default,
  last: LastName_default,
  fullName: FullName_default,
  mobile: Mobile_default,
  email: Email_default,
  rockPersonId: RockPersonId_default,
  contactKey: ContactKey_default
};
var DataAttributeMaker = _DataAttributeMaker;
var DataAttributeMaker_default = DataAttributeMaker;

// src/DataAttributeProcessingContext.ts
var DataAttributeProcessingContext = class _DataAttributeProcessingContext {
  constructor(firstName, lastName, mobile, rockPersonId, contactKey, email) {
    this.firstName = firstName;
    this.lastName = lastName;
    this.mobile = mobile;
    this.rockPersonId = rockPersonId;
    this.contactKey = contactKey;
    this.email = email;
  }
  static getPersonalSampleContext() {
    return new _DataAttributeProcessingContext(
      "John",
      "Doe",
      "+12001234567",
      "123456",
      "VZOEgpYw",
      "john.doe@clst.io"
    );
  }
  static getImpersonalSampleContext() {
    return new _DataAttributeProcessingContext(null, null, "+12001234567", null, null, null);
  }
  getFirstName() {
    return (this.firstName ?? "").trim() || null;
  }
  getLastName() {
    return (this.lastName ?? "").trim() || null;
  }
  getFullName() {
    return `${this.getFirstName() ?? ""} ${this.getLastName() ?? ""}`.trim() || null;
  }
  getMobile() {
    return this.mobile;
  }
  getRockPersonId() {
    return this.rockPersonId;
  }
  getContactKey() {
    return this.contactKey;
  }
  getEmail() {
    return (this.email ?? "").trim() || null;
  }
};
var DataAttributeProcessingContext_default = DataAttributeProcessingContext;

// src/DataAttributesDetector.ts
var _DataAttributesDetector = class _DataAttributesDetector {
  constructor() {
    this.dataAttributeMaker = new DataAttributeMaker_default();
  }
  processInText(text, processingStrategy) {
    text ?? (text = "");
    if (!text.includes("{{") || !text.includes("}}")) {
      return text;
    }
    text = text.replace(
      _DataAttributesDetector.REGEX,
      (substring, ...args) => {
        const groups = args[args.length - 1];
        let dataAttribute;
        try {
          dataAttribute = new DataAttributeMaker_default().makeFromTagContent(
            groups.tagContent
          );
        } catch (error) {
          if (error instanceof UnknownDataAttributeError_default) {
            return substring;
          }
          throw error;
        }
        const processedDataAttributeValue = processingStrategy.process(dataAttribute);
        if (processedDataAttributeValue === "") {
          return "";
        }
        return `${groups.spaceBefore ?? ""}${processedDataAttributeValue}`;
      }
    );
    return text;
  }
};
_DataAttributesDetector.REGEX = /(?<spaceBefore> )?{{(?<tagContent>[^}{]+)}}/g;
var DataAttributesDetector = _DataAttributesDetector;
var DataAttributesDetector_default = DataAttributesDetector;

// src/dataAttributeProcessingStrategies/ContextAwareDataAttributeProcessingStrategy.ts
var ContextAwareDataAttributeProcessingStrategy = class {
  constructor(context) {
    this.context = context;
  }
  process(dataAttribute) {
    return dataAttribute.getProcessedValueFromContext(this.context);
  }
};
var ContextAwareDataAttributeProcessingStrategy_default = ContextAwareDataAttributeProcessingStrategy;

// src/dataAttributeProcessingStrategies/LongestValueForReserveDataAttributeProcessingStrategy.ts
var LongestValueForReserveDataAttributeProcessingStrategy = class {
  process(dataAttribute) {
    return dataAttribute.getLongestValueForReserve();
  }
};
var LongestValueForReserveDataAttributeProcessingStrategy_default = LongestValueForReserveDataAttributeProcessingStrategy;

// src/generateDataAttributeTag.ts
var generateDataAttributeTag_default = (keyword, options) => {
  const optionsString = Object.entries(options ?? {}).map(([key, value]) => `|${value === "" ? key : `${key}:${value}`}`).join("");
  return `{{${keyword}${optionsString}}}`;
};
export {
  ContextAwareDataAttributeProcessingStrategy_default as ContextAwareDataAttributeProcessingStrategy,
  DataAttributeMaker_default as DataAttributeMaker,
  DataAttributeProcessingContext_default as DataAttributeProcessingContext,
  DataAttributesDetector_default as DataAttributesDetector,
  LongestValueForReserveDataAttributeProcessingStrategy_default as LongestValueForReserveDataAttributeProcessingStrategy,
  generateDataAttributeTag_default as generateDataAttributeTag
};
